import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { CheckCircle2, AlertTriangle, Loader2, BarChart3, Download, Trophy } from "lucide-react";
import { fetchAdPinLookup, fetchAdReport, downloadAdReportCsv, downloadAdReportPdf, fmtCtr } from "@/lib/api";
import { ReachTrend } from "@/components/vendor/ReachTrend";
import { ShareReportPanel } from "@/components/vendor/ShareReportPanel";
import { toast } from "sonner";
import { useAdStrings, AD_STRINGS } from "@/lib/adStrings";

const FLAG = {
  verified: { cls: "text-emerald-700", icon: CheckCircle2, text: "Verified" },
  unknown: { cls: "text-rose-600", icon: AlertTriangle, text: "Unknown PIN — not found in India Post records" },
  unverified: { cls: "text-amber-700", icon: AlertTriangle, text: "Not verified — lookup unavailable, please re-check" },
  invalid: { cls: "text-rose-600", icon: AlertTriangle, text: "Invalid PIN format" },
};

// Simple pre-payment coverage list: verified city/district for each targeted PIN; unknown ones clearly flagged.
export function AdCoverageList({ pins }) {
  const C = useAdStrings().mgr.cov;
  const [rows, setRows] = useState(null);
  const key = (pins || []).join(",");
  useEffect(() => {
    if (!key) return;
    setRows(null);
    fetchAdPinLookup(key).then((d) => setRows(d.results)).catch(() => setRows(pins.map((p) => ({ pincode: p, status: "unverified" }))));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!key) return null;
  const flagged = (rows || []).filter((r) => r.status !== "verified").length;
  return (
    <div className="mt-3 rounded-xl border border-slate-100 bg-white p-3" data-testid="ad-coverage-list">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{C.title(pins.length)}</div>
      {!rows ? <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {C.checking}</div> : (
        <ul className="space-y-1">
          {rows.map((r) => {
            const f = FLAG[r.status] || FLAG.unverified;
            const Icon = f.icon;
            return (
              <li key={r.pincode} className="flex items-start gap-2 text-xs" data-testid={`ad-coverage-${r.pincode}`}>
                <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${f.cls}`} />
                <span className="font-semibold text-slate-800 w-14 flex-shrink-0">{r.pincode}</span>
                <span className="min-w-0 text-slate-600" data-testid={`ad-coverage-status-${r.pincode}`}>
                  {r.status === "verified" ? `${r.district}${r.state ? `, ${r.state}` : ""}${r.areas?.length ? ` · ${r.areas.filter(Boolean).slice(0, 3).join(", ")}` : ""}` : <span className={f.cls}>{C[r.status] || C.unverified}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {flagged > 0 && <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 rounded-lg px-2 py-1" data-testid="ad-coverage-warning">{C.warn(flagged)}</div>}
      <div className="mt-1.5 text-[10px] text-slate-400">{C.foot}</div>
    </div>
  );
}

const NA = ({ label = "Not tracked" }) => <span className="text-slate-400 italic">{label}</span>;
const fmtD = (d, locale) => (d ? new Date(d).toLocaleDateString(locale) : null);
const dateCls = "border border-slate-200 rounded-lg px-2 py-1 text-[11px] bg-white outline-none focus:border-emerald-500 min-w-0";

export function DownloadButton({ adId, range, S, kind }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await (kind === "pdf" ? downloadAdReportPdf : downloadAdReportCsv)(adId, range); } catch { toast.error(kind === "pdf" ? S.pdfErr : S.csvErr); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white hover:bg-emerald-50 text-emerald-800 px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50" data-testid={`ad-report-${kind}-${adId}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} {kind === "pdf" ? S.pdf : S.csv}
    </button>
  );
}

export function DateFilter({ adId, draft, setDraft, onApply, onClear, active, S }) {
  return (
    <div className="flex items-end gap-1.5 flex-wrap mb-2" data-testid={`ad-report-date-filter-${adId}`}>
      <label className="text-[10px] text-slate-500 flex flex-col">{S.from}<input type="date" value={draft.from} max={draft.to || undefined} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} className={dateCls} data-testid={`ad-report-from-${adId}`} /></label>
      <label className="text-[10px] text-slate-500 flex flex-col">{S.to}<input type="date" value={draft.to} min={draft.from || undefined} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} className={dateCls} data-testid={`ad-report-to-${adId}`} /></label>
      <button type="button" onClick={onApply} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1 text-[11px] font-semibold" data-testid={`ad-report-apply-${adId}`}>{S.apply}</button>
      {active && <button type="button" onClick={onClear} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600" data-testid={`ad-report-clear-${adId}`}>{S.allTime}</button>}
    </div>
  );
}

export const EN_L = AD_STRINGS.en.L;

export function WeeklyChart({ adId, weekly, pins, L = EN_L, locale }) {
  const [pin, setPin] = useState("all");
  const data = useMemo(() => {
    const m = {};
    weekly.filter((w) => pin === "all" || w.pincode === pin).forEach((w) => {
      const r = (m[w.week_start] ||= { week: w.week_start, impressions: 0, views: 0, clicks: 0 });
      r.impressions += w.impressions; r.views += w.views || 0; r.clicks += w.clicks;
    });
    return Object.values(m).sort((a, b) => a.week.localeCompare(b.week)).map((r) => ({ ...r, label: new Date(r.week).toLocaleDateString(locale, { day: "numeric", month: "short" }) }));
  }, [weekly, pin, locale]);
  return (
    <div className="mb-3 rounded-lg bg-white border border-slate-100 p-2" data-testid={`ad-report-weekly-${adId}`}>
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="text-[11px] font-semibold text-slate-600">{L.weekly}</div>
        <select value={pin} onChange={(e) => setPin(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-0.5 text-[11px] bg-white" data-testid={`ad-report-weekly-pin-${adId}`}>
          <option value="all">{L.allPins}</option>
          {pins.map((p) => <option key={p.pincode} value={p.pincode}>{p.pincode}</option>)}
        </select>
      </div>
      {data.length === 0 ? <div className="text-[11px] text-slate-400 italic py-4 text-center" data-testid={`ad-report-weekly-empty-${adId}`}>{L.noData}</div> : (
        <div className="h-40 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="impressions" name={L.impressions} fill="#047857" radius={[3, 3, 0, 0]} />
              <Bar dataKey="views" name={L.views || "Views"} fill="#0ea5e9" radius={[3, 3, 0, 0]} />
              <Bar dataKey="clicks" name={L.clicks} fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function BestPin({ adId, rep, L = EN_L }) {
  if (!rep.has_data) return null;
  return (
    <div className="mb-2 text-[11px] inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-100 text-amber-900 px-2.5 py-1" data-testid={`ad-report-best-pin-${adId}`}>
      <Trophy className="w-3 h-3" />
      {rep.best_pins.length ? <>{L.topPin} <b>{rep.best_pins.join(", ")}</b></> : L.noClicks}
    </div>
  );
}

export function PinTable({ rep, compact = false, L = EN_L }) {
  const best = new Set(rep.best_pins);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs whitespace-nowrap">
        <thead><tr className="text-slate-500 text-left"><th className="py-1 pe-3">{L.pin}</th><th className="py-1 pe-3">{L.area}</th><th className="py-1 pe-3">{L.impressions}</th><th className="py-1 pe-3">{L.views || "Views"}</th>{!compact && <th className="py-1 pe-3">{L.unique}</th>}<th className="py-1 pe-3">{L.clicks}</th><th className="py-1 pe-3">{L.whatsapp || "WhatsApp clicks"}</th><th className="py-1 pe-3">{L.ctr}</th>{!compact && <><th className="py-1 pe-3">{L.listing}</th><th className="py-1">{L.notif}</th></>}</tr></thead>
        <tbody>
          {rep.pins.map((p) => (
            <tr key={p.pincode} className={`border-t border-slate-100 ${best.has(p.pincode) ? "bg-amber-50/70" : ""}`} data-testid={`ad-report-row-${p.pincode}`}>
              <td className="py-1 pe-3 font-semibold">{p.pincode}{best.has(p.pincode) && <Trophy className="inline w-3 h-3 ms-1 text-amber-600" data-testid={`ad-report-best-icon-${p.pincode}`} />}</td>
              <td className="py-1 pe-3 max-w-[10rem] truncate" title={[p.locality, p.district, p.state].filter(Boolean).join(", ")}>{p.district ? `${p.district}, ${p.state}` : <NA label={L.unverified} />}</td>
              <td className="py-1 pe-3">{p.impressions}</td><td className="py-1 pe-3" data-testid={`ad-report-views-${p.pincode}`}>{p.views ?? 0}</td>{!compact && <td className="py-1 pe-3" data-testid={`ad-report-reach-${p.pincode}`}>{p.unique_reach ?? p.unique_viewers}</td>}
              <td className="py-1 pe-3 font-semibold">{p.clicks}</td>
              <td className="py-1 pe-3" data-testid={`ad-report-wa-${p.pincode}`}>{p.whatsapp_clicks ?? 0}</td>
              <td className="py-1 pe-3" data-testid={`ad-report-ctr-${p.pincode}`}>{p.ctr === null || p.ctr === undefined ? L.na : fmtCtr(p.ctr)}</td>
              {!compact && <>
                <td className="py-1 pe-3" data-testid={`ad-report-listing-clicks-${p.pincode}`}>{p.clicks_listing === null ? <NA label={L.notTracked} /> : p.clicks_listing}</td>
                <td className="py-1">{p.clicks_notifications}</td>
              </>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_LABEL = { active: "Active", paused: "Paused", expired: "Completed", rejected: "Rejected", cancelled: "Cancelled",
  pending: "Pending", pending_approval: "Pending approval", pending_payment: "Pending payment" };

// Overall (all selected PINs together). Reach is distinct visitors across PINs, not a sum of the PIN rows.
function OverallSummary({ adId, rep, locale }) {
  const notDelivered = ["pending", "pending_approval", "pending_payment", "rejected", "cancelled"].includes(rep.status);
  const reach = rep.totals.unique_reach;
  const Box = ({ label, value, testid }) => (
    <div className="rounded-lg bg-white border border-slate-100 px-3 py-2 min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-base font-bold text-slate-900 truncate">{value}</div>
    </div>
  );
  return (
    <div className="mb-2" data-testid={`ad-report-overall-${adId}`}>
      <div className="text-[11px] font-semibold text-slate-600 mb-1">Overall — all {rep.pins.length} selected PIN{rep.pins.length === 1 ? "" : "s"} together</div>
      <div className="grid grid-cols-1 min-[360px]:grid-cols-3 gap-2">
        <Box label="Status" value={STATUS_LABEL[rep.status] || rep.status} testid={`ad-report-status-${adId}`} />
        <Box label="Total impressions" value={notDelivered ? "0 (not delivered)" : rep.totals.impressions} testid={`ad-report-total-impressions-${adId}`} />
        <Box label="Total unique reach" value={notDelivered ? "0 (not delivered)" : (reach ?? "—")} testid={`ad-report-total-reach-${adId}`} />
      </div>
      <div className="text-[10px] text-slate-500 mt-1" data-testid={`ad-report-reach-note-${adId}`}>
        Impressions = times the ad was displayed (sent in a PIN-filtered listing or Notifications). Unique reach = distinct visitors; repeat views by the same visitor count once in this period.
        {rep.reach_tracking_since ? ` Anonymous-visitor reach is recorded since ${new Date(rep.reach_tracking_since).toLocaleString(locale)}; earlier reach covers logged-in customers only (historical anonymous reach unavailable).` : " Historical reach unavailable."}
        {" "}PIN-wise figures are in the table below.
      </div>
    </div>
  );
}

export function AdReport({ adId, canShare = false }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [draft, setDraft] = useState({ from: "", to: "" });
  const S = useAdStrings();
  const hiLang = S.locale === "hi-IN";
  useEffect(() => {
    setErr("");
    fetchAdReport(adId, range).then(setRep).catch((e) => {
      const d = e?.response?.data?.detail;
      setErr(hiLang ? (e?.response?.status === 400 ? S.rangeErr : S.loadErr) : (typeof d === "string" ? d : S.loadErr));
    });
  }, [adId, range]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = () => {
    if (draft.from && draft.to && draft.from > draft.to) { toast.error(S.rangeErr); return; }
    setRange({ ...draft });
  };
  const filtered = !!(range.from || range.to);
  if (err && !rep) return <div className="text-xs text-rose-600 mt-2" data-testid={`ad-report-error-${adId}`}>{err}</div>;
  if (!rep) return <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {S.loading}</div>;
  const ctrTxt = rep.totals.ctr === null || rep.totals.ctr === undefined ? S.L.na : fmtCtr(rep.totals.ctr);
  return (
    <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50/60 p-3 min-w-0" data-testid={`ad-report-${adId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1" data-testid={`ad-report-totals-${adId}`}><BarChart3 className="w-3.5 h-3.5 text-emerald-700" /> {S.performance} · {rep.has_data ? `${rep.totals.impressions} ${S.impressions} · ${rep.totals.views ?? 0} ${S.views || "views"} · ${rep.totals.clicks} ${S.clicks} · ${rep.totals.whatsapp_clicks ?? 0} ${S.whatsapp || "WhatsApp clicks"} · ${ctrTxt} ${S.ctrShort}` : S.L.noData}</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <DownloadButton adId={adId} range={range} S={S} kind="csv" />
          <DownloadButton adId={adId} range={range} S={S} kind="pdf" />
          {canShare && <ShareReportPanel adId={adId} />}
        </div>
      </div>
      <DateFilter adId={adId} draft={draft} setDraft={setDraft} onApply={apply} onClear={() => { setDraft({ from: "", to: "" }); setRange({ from: "", to: "" }); }} active={filtered} S={S} />
      {err && <div className="text-[11px] text-rose-600 mb-1">{err}</div>}
      <OverallSummary adId={adId} rep={rep} locale={S.locale} />
      <div className="text-[10px] text-slate-500 mb-1.5" data-testid={`ad-report-period-${adId}`}>
        {filtered ? `${S.showing} ${range.from ? fmtD(range.from, S.locale) : S.start} – ${range.to ? fmtD(range.to, S.locale) : S.today}` : rep.period_from ? `${S.period} ${fmtD(rep.period_from, S.locale)} – ${fmtD(rep.period_to, S.locale)}` : S.notStarted}
      </div>
      {!rep.has_data ? (
        <div className="rounded-lg bg-white border border-dashed border-slate-200 text-center text-xs text-slate-500 py-5" data-testid={`ad-report-no-data-${adId}`}>{S.L.noData}{filtered ? S.forDates : ""}</div>
      ) : (
        <>
          <BestPin adId={adId} rep={rep} L={S.L} />
          <WeeklyChart adId={adId} weekly={rep.weekly} pins={rep.pins} L={S.L} locale={S.locale} />
          <ReachTrend adId={adId} trend={rep.reach_trend} pins={rep.pins} since={rep.reach_tracking_since} S={S} />
          <PinTable rep={rep} L={S.L} />
        </>
      )}
      <div className="text-[10px] text-slate-400 mt-1.5" data-testid={`ad-report-note-${adId}`}>{S.note} {rep.tracking_since ? `${S.firstEvent} ${new Date(rep.tracking_since).toLocaleString(S.locale)}.` : ""} {rep.listing_clicks_tracking_since && `${S.listingSince} ${new Date(rep.listing_clicks_tracking_since).toLocaleString(S.locale)}.`} {S.loggedIn}</div>
    </div>
  );
}
